/**
 * R138 · Feb 2026 — Lead Import Merge Review
 *
 * After a bulk import, this page surfaces every existing lead whose fields
 * were changed by the incoming data. For each row, the user sees the
 * previous value vs the new value side-by-side and picks which value
 * should win for each field. "Incoming" leaves the just-committed value
 * in place; "Existing" reverts that field.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function LeadsMergeReview() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [diffs, setDiffs] = useState([]);
  const [picks, setPicks] = useState({});     // { existing_id: { field: "existing"|"incoming" } }
  const [applied, setApplied] = useState({}); // { existing_id: true } once merged

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/imports/${sessionId}/diffs`);
      setDiffs(data.diffs || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load diffs")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (sessionId) load(); }, [sessionId]); // eslint-disable-line

  const setPick = (existingId, field, choice) => {
    setPicks((prev) => ({
      ...prev,
      [existingId]: { ...(prev[existingId] || {}), [field]: choice },
    }));
  };

  const applyOne = async (d) => {
    const picksForRow = picks[d.existing_id] || {};
    // Every field defaults to "incoming" (leave the just-committed value).
    // Send only the ones flipped to "existing" — that's what the backend uses.
    const filtered = {};
    Object.entries(picksForRow).forEach(([k, v]) => { if (v === "existing") filtered[k] = "existing"; });
    try {
      const { data } = await api.post(`/imports/apply-merge`, {
        session_id: sessionId,
        existing_id: d.existing_id,
        picks: filtered,
      });
      setApplied((prev) => ({ ...prev, [d.existing_id]: true }));
      const reverted = (data.reverted_fields || []).length;
      toast.success(reverted ? `Reverted ${reverted} field${reverted === 1 ? "" : "s"}` : "Kept all incoming values");
    } catch (e) { toast.error(apiErrorMessage(e, "Merge failed")); }
  };

  const applyAllKeepIncoming = () => {
    // Nothing to revert — mark every diff as reviewed. Fast-path so users
    // can accept the import wholesale after eyeballing the list.
    const next = {}; diffs.forEach((d) => { next[d.existing_id] = true; });
    setApplied(next);
    toast.success(`Marked ${diffs.length} row${diffs.length === 1 ? "" : "s"} as reviewed`);
  };

  const remaining = useMemo(() => diffs.filter((d) => !applied[d.existing_id]).length, [diffs, applied]);

  return (
    <div className="space-y-4 px-4 sm:px-6 md:px-10" data-testid="leads-merge-review-page">
      <PageHeader
        breadcrumb="CRM · Leads · Import"
        title="Merge review"
        description="These existing leads were updated by the import. Pick which value should win for each changed field — Incoming keeps the just-imported value, Existing reverts to what was stored before."
        testid="leads-merge-review-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none"
                    onClick={() => nav("/app/leads/import-history")}
                    data-testid="leads-merge-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to history
            </Button>
            {diffs.length > 0 && (
              <Button className="rounded-none" onClick={applyAllKeepIncoming}
                      data-testid="leads-merge-accept-all">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Accept all incoming
              </Button>
            )}
          </div>
        }
      />

      <div className="border border-border p-3 bg-muted/20 flex items-center gap-4 text-xs">
        <div><b>{diffs.length}</b> merged rows</div>
        <div><b>{diffs.length - remaining}</b> reviewed</div>
        <div className={remaining > 0 ? "text-amber-700" : "text-emerald-700"}>
          <b>{remaining}</b> remaining
        </div>
      </div>

      {busy && diffs.length === 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading diffs…
        </div>
      )}

      {!busy && diffs.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
             data-testid="leads-merge-empty">
          <AlertTriangle className="w-5 h-5 mx-auto mb-2 opacity-60" />
          No merges to review. Either nothing was updated by this import or the session has no committed rows.
        </div>
      )}

      <div className="space-y-3">
        {diffs.map((d) => (
          <div key={d.existing_id}
               className={`border ${applied[d.existing_id] ? "border-emerald-300 bg-emerald-50/30" : "border-border"} p-3`}
               data-testid={`merge-row-${d.existing_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="font-semibold text-sm">{d.name || d.existing_id.slice(0, 8)}</div>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {d.diff_fields.length} field{d.diff_fields.length === 1 ? "" : "s"} changed
              </span>
              <div className="flex-1" />
              {applied[d.existing_id] ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] uppercase tracking-widest">
                  <CheckCircle2 className="w-3 h-3" /> Reviewed
                </span>
              ) : (
                <Button size="sm" className="rounded-none h-7 text-[11px]"
                        onClick={() => applyOne(d)}
                        data-testid={`merge-apply-${d.existing_id}`}>
                  Apply picks
                </Button>
              )}
            </div>
            <table className="w-full text-xs" data-testid={`merge-table-${d.existing_id}`}>
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[130px]">Field</th>
                  <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Existing</th>
                  <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Incoming (imported)</th>
                  <th className="text-center px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[130px]">Winner</th>
                </tr>
              </thead>
              <tbody>
                {d.diff_fields.map((f) => {
                  const chosen = (picks[d.existing_id] || {})[f] || "incoming";
                  const cell = (val) => (val === null || val === undefined || val === "" ? <span className="text-muted-foreground italic">(empty)</span> : String(val));
                  return (
                    <tr key={f} className="border-t border-border">
                      <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">{f}</td>
                      <td className={`px-2 py-1 ${chosen === "existing" ? "bg-amber-50" : ""}`}>{cell(d.existing[f])}</td>
                      <td className={`px-2 py-1 ${chosen === "incoming" ? "bg-emerald-50" : ""}`}>{cell(d.incoming[f])}</td>
                      <td className="px-2 py-1 text-center">
                        <div className="inline-flex border border-border">
                          <button type="button"
                                  onClick={() => setPick(d.existing_id, f, "existing")}
                                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${chosen === "existing" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                                  data-testid={`merge-pick-existing-${d.existing_id}-${f}`}
                                  disabled={applied[d.existing_id]}>
                            Existing
                          </button>
                          <button type="button"
                                  onClick={() => setPick(d.existing_id, f, "incoming")}
                                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border-l border-border ${chosen === "incoming" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                                  data-testid={`merge-pick-incoming-${d.existing_id}-${f}`}
                                  disabled={applied[d.existing_id]}>
                            Incoming
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
